import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { ArrowLeft, Loader2, RefreshCw, Mail, Phone, MapPin, ExternalLink, Check, X, Clock, PackageCheck } from "lucide-react";

const STATUS = {
  new: { label: "Nuova", color: "bg-[#F4E500]/70 text-[#1F2937]" },
  review: { label: "In valutazione", color: "bg-[#DBEAFE] text-[#1E40AF]" },
  approved: { label: "Approvata", color: "bg-[#E8F5EC] text-[#0E7C3D]" },
  rejected: { label: "Rifiutata", color: "bg-[#FEE2E2] text-[#B91C1C]" },
};

const StatusPill = ({ status }) => {
  const s = STATUS[status] || STATUS.new;
  return <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${s.color}`}>{s.label}</span>;
};

const MembershipRequests = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await adminApi.listMembership(filter || undefined);
      setRows(r.data || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore nel caricamento.");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    setBusy(true);
    try {
      await adminApi.updateMembership(id, { status });
      await load();
      if (selected?.id === id) {
        const r = await adminApi.getMembership(id);
        setSelected(r.data);
      }
    } catch (e) { setError(e?.response?.data?.detail || "Errore aggiornamento stato."); }
    finally { setBusy(false); }
  };

  const convert = async (id) => {
    if (!window.confirm("Vuoi trasformare questa richiesta in una nuova scheda attività (stato bozza)?")) return;
    setBusy(true);
    try {
      const r = await adminApi.convertMembership(id);
      const bizId = r.data?.business?.id;
      if (bizId) navigate(`/admin/attivita/${bizId}`);
    } catch (e) { setError(e?.response?.data?.detail || "Errore conversione."); }
    finally { setBusy(false); }
  };

  const openDetail = async (row) => {
    setSelected(row);
    try {
      const r = await adminApi.getMembership(row.id);
      setSelected(r.data);
    } catch (e) { /* keep row */ }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-[#12964A] text-sm">
            <ArrowLeft className="w-4 h-4" /> Pannello
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={load} className="inline-flex items-center gap-2 border border-[#E5E7EB] rounded-full px-4 py-1.5 text-sm hover:border-slate-400">
              <RefreshCw className="w-4 h-4" /> Aggiorna
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-2">Adesioni</div>
          <h1 className="font-display font-bold text-4xl md:text-5xl text-[#1F2937]">Richieste di adesione</h1>
          <p className="text-slate-500 mt-2">Gestisci le richieste in arrivo dalla pagina pubblica.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {[["", "Tutte"], ["new", "Nuove"], ["review", "In valutazione"], ["approved", "Approvate"], ["rejected", "Rifiutate"]].map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-full text-sm border ${filter === k ? "bg-[#12964A] text-white border-[#12964A]" : "bg-white text-[#1F2937] border-[#E5E7EB] hover:border-[#12964A]"}`} data-testid={`filter-${k || "all"}`}>{l}</button>
          ))}
        </div>

        {error && <div className="mb-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 bg-white rounded-3xl border border-[#E5E7EB] overflow-hidden">
            {loading ? (
              <div className="p-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#12964A]" /></div>
            ) : rows.length === 0 ? (
              <div className="p-16 text-center text-slate-500">Nessuna richiesta {filter ? "con questo stato" : "ancora"}.</div>
            ) : (
              <div className="divide-y divide-[#F1F5F9]">
                {rows.map((r) => (
                  <button key={r.id} onClick={() => openDetail(r)} className={`w-full text-left p-5 hover:bg-[#F8F9FA] flex items-start justify-between gap-4 ${selected?.id === r.id ? "bg-[#F0FAF3]" : ""}`}>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="font-medium text-[#1F2937] truncate">{r.business_name}</div>
                        <StatusPill status={r.status} />
                      </div>
                      <div className="text-xs text-slate-500 truncate">{r.first_name} {r.last_name} · {r.email}</div>
                    </div>
                    <div className="text-xs text-slate-400 flex-shrink-0 flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {new Date(r.created_at).toLocaleDateString("it-IT")}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          <aside className="lg:col-span-5">
            {selected ? (
              <div className="bg-white rounded-3xl border border-[#E5E7EB] p-6 sticky top-24">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mb-1">Richiesta</div>
                    <h2 className="font-display font-bold text-xl text-[#1F2937]">{selected.business_name}</h2>
                  </div>
                  <StatusPill status={selected.status} />
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-2 text-slate-700"><span className="text-slate-500 w-20">Nome:</span><span>{selected.first_name} {selected.last_name}</span></div>
                  {selected.company_name && <div className="flex items-start gap-2 text-slate-700"><span className="text-slate-500 w-20">Ragione:</span><span>{selected.company_name}</span></div>}
                  {selected.category_slug && <div className="flex items-start gap-2 text-slate-700"><span className="text-slate-500 w-20">Categoria:</span><span>{selected.category_slug}{selected.subcategory_slug ? ` / ${selected.subcategory_slug}` : ""}</span></div>}
                  {selected.address && <div className="flex items-start gap-2 text-slate-700"><MapPin className="w-4 h-4 text-[#12964A] mt-0.5" />{selected.address}</div>}
                  {selected.phone && <div className="flex items-start gap-2 text-slate-700"><Phone className="w-4 h-4 text-[#12964A] mt-0.5" /><a href={`tel:${selected.phone}`} className="hover:underline">{selected.phone}</a></div>}
                  <div className="flex items-start gap-2 text-slate-700"><Mail className="w-4 h-4 text-[#12964A] mt-0.5" /><a href={`mailto:${selected.email}`} className="hover:underline">{selected.email}</a></div>
                  {selected.website && <div className="flex items-start gap-2 text-slate-700"><ExternalLink className="w-4 h-4 text-[#12964A] mt-0.5" /><a href={selected.website} target="_blank" rel="noreferrer" className="hover:underline truncate">{selected.website}</a></div>}
                  {selected.short_description && (
                    <div className="mt-3 p-3 bg-[#F8F9FA] rounded-xl text-slate-700 text-sm">
                      {selected.short_description}
                    </div>
                  )}
                  {selected.logo_url && (
                    <div className="pt-3">
                      <div className="text-xs text-slate-500 mb-2">Logo</div>
                      <img src={selected.logo_url} alt="" className="w-24 h-24 rounded-xl object-cover border border-[#E5E7EB]" />
                    </div>
                  )}
                  {selected.gallery?.length > 0 && (
                    <div className="pt-3">
                      <div className="text-xs text-slate-500 mb-2">Galleria ({selected.gallery.length})</div>
                      <div className="grid grid-cols-3 gap-2">
                        {selected.gallery.slice(0, 6).map((g) => (
                          <img key={g.url} src={g.url} alt="" className="aspect-square w-full rounded-lg object-cover border border-[#E5E7EB]" />
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 pt-6 border-t border-[#E5E7EB]">
                  {selected.converted_business_id ? (
                    <div className="bg-[#E8F5EC] text-[#0E7C3D] rounded-xl p-4 text-sm">
                      <div className="font-medium mb-1">Già convertita in scheda attività</div>
                      <Link to={`/admin/attivita/${selected.converted_business_id}`} className="inline-flex items-center gap-1 text-xs hover:underline">Apri scheda →</Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => setStatus(selected.id, "review")} disabled={busy || selected.status === "review"} className="px-3 py-1.5 rounded-full text-xs border border-[#E5E7EB] hover:border-slate-400 disabled:opacity-40" data-testid="btn-review">In valutazione</button>
                        <button onClick={() => setStatus(selected.id, "rejected")} disabled={busy} className="px-3 py-1.5 rounded-full text-xs border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-40" data-testid="btn-reject"><X className="w-3 h-3 inline mr-1" /> Rifiuta</button>
                        <button onClick={() => setStatus(selected.id, "approved")} disabled={busy || selected.status === "approved"} className="px-3 py-1.5 rounded-full text-xs bg-[#E8F5EC] text-[#0E7C3D] hover:bg-[#D0EBD8] disabled:opacity-40" data-testid="btn-approve"><Check className="w-3 h-3 inline mr-1" /> Approva</button>
                      </div>
                      <button onClick={() => convert(selected.id)} disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-[#12964A] hover:bg-[#0E7C3D] disabled:opacity-40 text-white rounded-full px-4 py-2.5 text-sm" data-testid="btn-convert">
                        <PackageCheck className="w-4 h-4" /> Converti in scheda attività
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-[#E5E7EB] p-10 text-center text-slate-500 sticky top-24">
                Seleziona una richiesta per vederne i dettagli.
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
};

export default MembershipRequests;
